import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { submitConsultation } from "../lib/api";
import { SERVICES } from "../lib/services";

export function ConsultationDialog({ trigger, defaultService }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: defaultService || "",
    preferred_time: "",
    message: "",
  });

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email & phone.");
      return;
    }
    try {
      setBusy(true);
      const payload = { ...form };
      if (!payload.service) delete payload.service;
      await submitConsultation(payload);
      toast.success("Consultation requested", {
        description:
          "Our advisory team will reach out within 1 business day.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        service: defaultService || "",
        preferred_time: "",
        message: "",
      });
      setOpen(false);
    } catch (err) {
      toast.error("Submission failed", {
        description: "Please try again or call +91 98927 59155.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="consultation-dialog"
        className="sm:max-w-[560px] bg-[#FAF5EB] border-slate-200/10 text-[#2A0A0E] p-0 overflow-hidden"
      >
        <div className="p-7">
          <DialogHeader>
            <p className="eyebrow">Private Consultation</p>
            <DialogTitle className="h-display-3 mt-2 text-[#2A0A0E] font-display">
              Speak with an advisor
            </DialogTitle>
            <DialogDescription className="text-slate-600/60 mt-2">
              A confidential, no-obligation conversation to understand your
              goals and recommend the right structure.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600/50 mb-1.5 block">
                  Full name
                </label>
                <Input
                  data-testid="consultation-name-input"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] focus:ring-[#7A1F26]/20 text-[#2A0A0E] rounded-none h-11"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600/50 mb-1.5 block">
                  Phone
                </label>
                <Input
                  data-testid="consultation-phone-input"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 98xxxxxxxx"
                  className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] focus:ring-[#7A1F26]/20 text-[#2A0A0E] rounded-none h-11"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-600/50 mb-1.5 block">
                Email
              </label>
              <Input
                type="email"
                data-testid="consultation-email-input"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] focus:ring-[#7A1F26]/20 text-[#2A0A0E] rounded-none h-11"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600/50 mb-1.5 block">
                  Service of interest
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((s) => ({ ...s, service: v }))}
                >
                  <SelectTrigger
                    data-testid="consultation-service-select"
                    className="bg-[#FAF5EB] border-slate-200/10 text-[#2A0A0E] rounded-none h-11"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FAF5EB] border-slate-200/10 text-[#2A0A0E]">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="General Advisory">
                      General Advisory
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600/50 mb-1.5 block">
                  Preferred time
                </label>
                <Input
                  data-testid="consultation-time-input"
                  value={form.preferred_time}
                  onChange={update("preferred_time")}
                  placeholder="e.g. Weekdays, 11am IST"
                  className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] focus:ring-[#7A1F26]/20 text-[#2A0A0E] rounded-none h-11"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-600/50 mb-1.5 block">
                Brief message (optional)
              </label>
              <Textarea
                data-testid="consultation-message-input"
                value={form.message}
                onChange={update("message")}
                placeholder="Anything specific you would like to discuss?"
                rows={3}
                className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] focus:ring-[#7A1F26]/20 text-[#2A0A0E] rounded-none"
              />
            </div>

            <DialogFooter className="pt-2 gap-3 sm:gap-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="consultation-cancel-button"
                className="btn-ghost-light"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="consultation-submit-button"
                className="btn-gold disabled:opacity-60"
              >
                {busy ? "Submitting..." : "Request Consultation"}
              </button>
            </DialogFooter>
            <p className="text-[11px] text-slate-600/40 mt-3">
              Your information stays confidential and is used only to schedule
              your consultation.
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
